import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import type { TaxCategory } from '../products/products.api';

export interface Purchase {
  id: string;
  supplierName: string;
  supplierDoc: string;
  docType: string;
  supplierDocNumber: string | null;
  issueDate: string;
  paymentTerm: string;
  dueDate: string | null;
  subtotal: string;
  igv: string;
  total: string;
  status: string;
}

export interface PurchaseLineInput {
  productId?: string | null;
  description: string;
  quantity: number;
  unitCost: number;
  taxCategory: TaxCategory;
}

export interface PurchaseInput {
  supplierPartyId?: string | null;
  supplierName: string;
  supplierDoc: string;
  docType: 'FACTURA' | 'BOLETA' | 'RECIBO' | 'OTRO';
  supplierDocNumber?: string;
  issueDate: string;
  paymentTerm: 'CONTADO' | 'CREDITO';
  dueDate?: string;
  warehouseId?: string | null;
  notes?: string;
  lines: PurchaseLineInput[];
}

export interface Payable {
  id: string;
  purchaseId: string;
  supplierName: string;
  supplierDoc: string;
  totalAmount: string;
  paidAmount: string;
  balance: string;
  currency: string;
  dueDate: string | null;
  daysOverdue: number;
}

export interface PayablesResponse {
  items: Payable[];
  totalOpen: string;
  aging: { current: string; d1_30: string; d31_60: string; d61_90: string; d90: string };
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

export function usePurchases(params: { page?: number; search?: string } = {}) {
  return useQuery({
    queryKey: ['purchases', params],
    queryFn: async () =>
      (await apiClient.get<{ items: Purchase[]; total: number }>('/purchases', { params })).data,
    retry: false,
  });
}

export function useCreatePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PurchaseInput) => (await apiClient.post('/purchases', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.invalidateQueries({ queryKey: ['payables'] });
      qc.invalidateQueries({ queryKey: ['stock'] });
      toast.success('Compra registrada');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo registrar la compra')),
  });
}

export function usePayables() {
  return useQuery({
    queryKey: ['payables'],
    queryFn: async () => (await apiClient.get<PayablesResponse>('/payables')).data,
    retry: false,
  });
}

export function usePaySupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      amount: number;
      method: string;
      reference?: string;
      paidAt?: string;
      accountId?: string;
    }) => (await apiClient.post(`/payables/${id}/payments`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payables'] });
      toast.success('Pago registrado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo registrar el pago')),
  });
}
